import React from 'react';
import PropTypes from 'prop-types';
import { Badge, Button } from '@opendate-app/setlist';

const fmt = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });

const scanLabel = (e) => {
  const action = e.eventType === 'check_out' ? 'Check-out' : 'Check-in';
  return e.status === 'failed' ? `Failed ${action.toLowerCase()}` : action;
};

const OrderTicketsCard = ({ tickets, canReassignSeat, onChangeSeat, onChangeType }) => (
  <div className="op-card">
    <h2 className="op-section-title">Transaction Items</h2>
    {tickets.length === 0 ? (
      <div className="op-muted">No tickets on this order.</div>
    ) : (
      <div className="op-table-scroll">
      <table className="op-table">
        <thead>
          <tr>
            <th>Type</th><th>Name</th><th>Barcode</th><th>Seat</th><th>Status</th><th>Price</th>{canReassignSeat && <th><span className="sr-only">Ticket actions</span></th>}
          </tr>
        </thead>
        <tbody>
          {tickets.map((t) => {
            const partiallyRefunded = !t.refunded && t.refundedAmount > 0;
            return (
              <tr key={`${t.kind}-${t.id}`} className={t.refunded ? 'op-row-refunded' : (partiallyRefunded ? 'op-row-partial-refunded' : '')}>
                <td data-label="Type">
                  {t.productName}
                  {t.resalePurchase && <div className="op-muted">Resale purchase</div>}
                </td>
                <td data-label="Name">{t.attendeeName || '—'}</td>
                <td data-label="Barcode">{t.displayBarcode || t.barcode}</td>
                <td data-label="Seat">{t.seatAssignment || '—'}</td>
                <td data-label="Status">
                  {t.resold ? (
                    <div className="op-muted">Resold</div>
                  ) : (
                    <div>{t.refunded ? 'Refunded' : (t.chargedBack ? 'Charged back' : (partiallyRefunded ? 'Partially refunded' : (t.checkedInAt ? 'Checked in' : 'Valid')))}</div>
                  )}
                  {t.refundFailureReason && (
                    <div data-testid="refund-failed">
                      <Badge color="error" size="sm">Refund failed</Badge>
                      <div className="op-muted" style={{ display: 'inline-block', marginLeft: 6 }}>{t.refundFailureReason.replace(/_/g, ' ')}</div>
                    </div>
                  )}
                  {t.checkedInAt && t.checkedInAtHuman && (
                    <div className="op-muted op-checkin-time">{t.checkedInAtHuman}</div>
                  )}
                  {t.ticketEvents && t.ticketEvents.length > 0 && (
                    <details className="op-scan-history">
                      <summary className="op-muted">Scan history ({t.ticketEvents.length})</summary>
                      <ul>
                        {t.ticketEvents.map((e, i) => (
                          <li key={i}>
                            {scanLabel(e)} — {e.at}
                            {e.failureReason ? ` (${e.failureReason})` : ''}
                          </li>
                        ))}
                      </ul>
                    </details>
                  )}
                </td>
                <td data-label="Price">
                  {t.finalCost != null ? fmt.format(t.finalCost) : '—'}
                  {partiallyRefunded && (
                    <span className="op-muted"> (−{fmt.format(t.refundedAmount)} refunded)</span>
                  )}
                </td>
                {canReassignSeat && (
                  <td data-label="" className="op-ticket-actions">
                    {t.seatMovable && onChangeSeat && (
                      <Button size="sm" aria-label={`Change seat ${t.seatAssignment || ''}`.trim()} onClick={() => onChangeSeat(t)}>Change seat</Button>
                    )}
                    {t.seatPickable && onChangeSeat && (
                      <Button size="sm" aria-label={`Pick a seat ${t.productName || ''}`.trim()} onClick={() => onChangeSeat(t)}>Pick a seat</Button>
                    )}
                    {t.typeChangeable && onChangeType && (
                      <Button size="sm" aria-label={`Change ticket type ${t.productName || ''}`.trim()} onClick={() => onChangeType(t)}>Change type</Button>
                    )}
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
      </div>
    )}
  </div>
);

OrderTicketsCard.propTypes = {
  tickets: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number.isRequired,
    kind: PropTypes.string,
    productName: PropTypes.string,
    attendeeName: PropTypes.string,
    barcode: PropTypes.string,
    displayBarcode: PropTypes.string,
    seatAssignment: PropTypes.string,
    checkedInAt: PropTypes.string,
    checkedInAtHuman: PropTypes.string,
    ticketEvents: PropTypes.arrayOf(PropTypes.shape({
      eventType: PropTypes.string,
      status: PropTypes.string,
      failureReason: PropTypes.string,
      at: PropTypes.string,
    })),
    finalCost: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    refunded: PropTypes.bool,
    chargedBack: PropTypes.bool,
    refundedAmount: PropTypes.number,
    refundFailureReason: PropTypes.string,
    resold: PropTypes.bool,
    resalePurchase: PropTypes.bool,
    seatsioObjectId: PropTypes.string,
    seatingCategoryKey: PropTypes.string,
    seatMovable: PropTypes.bool,
    seatPickable: PropTypes.bool,
    typeChangeable: PropTypes.bool,
    ticketTypeId: PropTypes.number,
  })).isRequired,
  canReassignSeat: PropTypes.bool,
  onChangeSeat: PropTypes.func,
  onChangeType: PropTypes.func,
};

OrderTicketsCard.defaultProps = {
  canReassignSeat: false,
  onChangeSeat: null,
  onChangeType: null,
};

export default OrderTicketsCard;
